import { Box, Flex, Icon, Text, useColorModeValue, useToken } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { Check, ChevronDown, ChevronRight, TerminalSquare } from 'lucide-react'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ImageTag, Reference, StreamBlock, ToolBlock } from '../../types/chatTypes'
import AnswerText from '../Answer/AnswerText'
import { graphToolLabelFamily } from '../../core/tools'

// Moving-highlight shimmer used on active tool labels — a lighter band sweeps
// left-to-right across the text, signalling that the step is in progress.
const shimmer = keyframes`
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`

interface StreamBlocksProps {
  blocks: StreamBlock[]
  references?: Reference[]
  images?: ImageTag[]
}

/**
 * Renders an answer as the ordered mix of text and retrieval steps it arrived
 * as, so a step stays anchored between the paragraphs it interrupted. Only
 * graph agents produce tool blocks; every other answer renders as plain text.
 */
export const StreamBlocks: React.FC<StreamBlocksProps> = ({ blocks, references, images }) => {
  return (
    <Box display='flex' flexDirection='column' gap={2}>
      {blocks.map((block, i) =>
        block.kind === 'text' ? (
          <AnswerText key={i} markdownContent={block.text} references={references} images={images} />
        ) : (
          <ToolBlockView key={block.id} block={block} />
        ),
      )}
    </Box>
  )
}

const ToolBlockView: React.FC<{ block: ToolBlock }> = ({ block }) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const greyText = useColorModeValue('gray.500', 'gray.400')
  const ruleColor = useColorModeValue('gray.300', 'gray.600')

  const [gray500, gray400, gray200, gray50] = useToken('colors', [
    'gray.500',
    'gray.400',
    'gray.200',
    'gray.50',
  ])
  const shimmerBase = useColorModeValue(gray500, gray400)
  const shimmerHi = useColorModeValue(gray200, gray50)

  const toggle = useCallback(() => setExpanded(v => !v), [])

  // Tool names carry the graph source key (`graph_search_<key>`), so fall back
  // to the prefix label and finally to the raw name rather than showing a blank.
  const labelKey = `tools.${block.name}.${block.status}`
  const fallback = t(`tools.${graphToolLabelFamily(block.name)}.${block.status}`, {
    defaultValue:
      block.status === 'active'
        ? t('tools.unknown.active', { name: block.name })
        : t('tools.unknown.done', { name: block.name }),
  })
  const label = t(labelKey, { defaultValue: fallback })

  const isActive = block.status === 'active'
  const shimmerSx = isActive
    ? {
        background: `linear-gradient(90deg, ${shimmerBase} 0%, ${shimmerHi} 50%, ${shimmerBase} 100%)`,
        backgroundSize: '200% 100%',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        color: 'transparent',
        animation: `${shimmer} 2s linear infinite`,
      }
    : undefined

  const query = typeof block.args?.query === 'string' ? block.args.query : undefined

  return (
    <Box my={1}>
      <Flex
        align='center'
        gap={1}
        cursor='pointer'
        onClick={toggle}
        userSelect='none'
        _hover={{ color: 'gray.700' }}
      >
        <Text fontSize='sm' color={greyText} sx={shimmerSx}>
          {label}
        </Text>
        <Icon as={expanded ? ChevronDown : ChevronRight} boxSize={4} color={greyText} />
      </Flex>

      {expanded && (
        <Box borderLeftWidth='2px' borderColor={ruleColor} pl={3} ml={1} mt={2} py={1}>
          <Flex align='center' gap={2} mb={2}>
            <Icon as={TerminalSquare} boxSize={4} color={greyText} />
            <Text fontSize='sm' color={greyText} sx={shimmerSx}>
              {label}
            </Text>
          </Flex>
          {query && (
            <Text fontSize='sm' color={greyText} mb={2} whiteSpace='pre-wrap'>
              {query}
            </Text>
          )}
          <Flex align='center' gap={2}>
            {block.status === 'done' ? (
              <Icon as={Check} boxSize={4} color='green.500' />
            ) : (
              <Box boxSize={2} borderRadius='full' bg='blue.400' />
            )}
            <Text fontSize='sm' color={greyText}>
              {block.status === 'done'
                ? `${t('tools.statusDone', { defaultValue: 'Klaar' })}${
                    block.durationMs != null ? ` · ${block.durationMs}ms` : ''
                  }`
                : t('tools.statusRunning', { defaultValue: 'Bezig…' })}
            </Text>
          </Flex>
        </Box>
      )}
    </Box>
  )
}

export default StreamBlocks
